import type { BuiltInExcelShade } from '../data/builtInExcelShades';

export type Shade = BuiltInExcelShade;

/** `#abcdef` / `abcdef` -> `#ABCDEF`; anything else -> null. */
export function normalizeHex(value: unknown): string | null {
  if (!value) return null;
  const text = String(value).trim();
  if (/^#[0-9a-f]{6}$/i.test(text)) return text.toUpperCase();
  if (/^[0-9a-f]{6}$/i.test(text)) return `#${text.toUpperCase()}`;
  return null;
}

const toHex = (r: number, g: number, b: number) =>
  `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;

/**
 * Rows from a sheet with a header row, e.g. `Shade Name | Shade code | HEX CODES | RED | GREEN | BLUE`.
 * Column names are matched loosely; HEX is derived from RGB when missing.
 */
export function parseShadeRecords(rows: Record<string, unknown>[]): Shade[] {
  const shades: Shade[] = [];
  for (const row of rows) {
    if (typeof row !== 'object' || !row) continue;
    let name = '';
    let code = '';
    let hex = '';
    const rgb: { r?: number; g?: number; b?: number } = {};

    for (const [key, raw] of Object.entries(row)) {
      if (raw === undefined || raw === null) continue;
      const column = key.trim().toLowerCase();
      const value = String(raw).trim();
      const channel = parseInt(value, 10);

      // HEX first: a "HEX CODES" column would otherwise be taken for the shade code.
      if (column.includes('hex')) hex = normalizeHex(value) ?? hex;
      else if (column.includes('code')) code = value;
      else if (column.includes('name')) {
        // With several name columns (UPPERCASE and Title Case), prefer Title Case.
        if (!name || (value !== value.toUpperCase() && name === name.toUpperCase())) name = value;
      } else if ((column === 'red' || column === 'r') && !isNaN(channel)) rgb.r = channel;
      else if ((column === 'green' || column === 'g') && !isNaN(channel)) rgb.g = channel;
      else if ((column === 'blue' || column === 'b') && !isNaN(channel)) rgb.b = channel;
    }

    const hasRgb = rgb.r !== undefined && rgb.g !== undefined && rgb.b !== undefined;
    if (!hex && hasRgb) hex = toHex(rgb.r!, rgb.g!, rgb.b!);
    if (!hex) continue;
    shades.push({
      name: name || code || `Shade ${shades.length + 1}`,
      code,
      hex,
      rgb: hasRgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : undefined,
    });
  }
  return shades;
}

const SHADE_CODE = [/^NP[\sA-Z0-9-]+$/i, /^\d{3,5}$/, /^[A-Z0-9-]{3,12}$/i];

/** Header-less sheets: scans each row for a HEX cell plus code-like and name-like cells. */
export function parseShadeGrid(rows: unknown[][]): Shade[] {
  const shades: Shade[] = [];
  for (const row of rows) {
    if (!Array.isArray(row)) continue;
    let hex: string | null = null;
    let name = '';
    let code = '';

    for (const cell of row) {
      if (!cell) continue;
      const cellHex = normalizeHex(cell);
      if (cellHex) {
        hex = cellHex;
        continue;
      }
      const text = String(cell).trim();
      if (text.length === 0 || text.length >= 60) continue;
      if (SHADE_CODE.some((pattern) => pattern.test(text))) {
        if (!code) code = text;
        else if (!name) name = text;
      } else if (!name && !/hex|rgb/i.test(text)) {
        name = text;
      }
    }

    if (hex) shades.push({ name: name || code || `Shade ${shades.length + 1}`, code, hex });
  }
  return shades;
}

/** Parses the first sheet of an .xlsx/.xls/.csv file. The parser library loads on first use. */
export async function parseShadeWorkbook(data: ArrayBuffer): Promise<Shade[]> {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(new Uint8Array(data), { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];
  const records = parseShadeRecords(XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet));
  return records.length > 0 ? records : parseShadeGrid(XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 }));
}

/** Appends shades not already present (same name, or same code), preserving order. */
export function mergeShades(base: Shade[], additions: Shade[]): Shade[] {
  const names = new Set(base.map((s) => s.name.toLowerCase()));
  const codes = new Set(base.filter((s) => s.code).map((s) => s.code.toLowerCase()));
  const merged = [...base];
  for (const shade of additions) {
    const name = shade.name.toLowerCase();
    const code = shade.code?.toLowerCase();
    if (names.has(name) || (code && codes.has(code))) continue;
    merged.push(shade);
    names.add(name);
    if (code) codes.add(code);
  }
  return merged;
}

/** Case-insensitive match on name, code or HEX. */
export function filterShades(shades: Shade[], query: string): Shade[] {
  const q = query.trim().toLowerCase();
  if (!q) return shades;
  return shades.filter(
    (s) => s.name.toLowerCase().includes(q) || s.code?.toLowerCase().includes(q) || s.hex.toLowerCase().includes(q),
  );
}
